/*
 * Keeps the header cart count in sync when a cart app changes the cart.
 * The Shiprocket (Fastrr) smart cart updates quantities and removes items with /cart/change.js without telling
 * the theme, so the header count went stale. Watch cart requests (and the drawer closing) and re-read /cart.js.
 * Requests are watched directly: the browser's resource timing log fills up on busy pages and stops reporting.
 */

const SMART_CART_OPEN_CLASS = 'srsc-overflow-hidden';

/** @type {number | undefined} */
let syncTimer;
let syncId = 0;

async function syncCartCount() {
  const id = ++syncId;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    // A newer refresh started while this one was in flight; let it win
    if (id !== syncId) return;
    document.querySelectorAll('cart-icon').forEach((icon) => {
      // renderCartBubble(count, comingFromProductForm, animate) sets the count, toggles the bubble and stores it
      /** @type {any} */ (icon).renderCartBubble?.(cart.item_count, false, false);
    });
  } catch (error) {
    console.warn('[cart-count-sync] Could not refresh the cart count:', error);
  }
}

function scheduleSync() {
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(syncCartCount, 300);
}

/**
 * Announces finished cart requests (from the theme or any app, fetch or XHR) as a `cart-request:done` event
 * on document. Installed once per page even when several scripts include it.
 */
function watchCartRequests() {
  const win = /** @type {any} */ (window);
  if (win.__cartRequestWatch) return;
  win.__cartRequestWatch = true;

  const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const notify = (/** @type {string} */ url) => {
    try {
      const path = new URL(url, window.location.origin).pathname;
      if (CART_WRITE.test(path)) document.dispatchEvent(new CustomEvent('cart-request:done', { detail: { path } }));
    } catch {
      // Not a URL we can read
    }
  };

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const request = originalFetch.apply(this, args);
    const input = /** @type {any} */ (args[0]);
    const url = typeof input === 'string' ? input : input?.url ?? String(input);
    request.then(
      () => notify(url),
      () => {}
    );
    return request;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (/** @type {string} */ method, /** @type {string | URL} */ url, ...rest) {
    this.addEventListener('loadend', () => notify(String(url)));
    return /** @type {any} */ (originalOpen).call(this, method, url, ...rest);
  };
}

// Any request that changes the cart, from the theme or an app
watchCartRequests();
document.addEventListener('cart-request:done', scheduleSync);

// Backup: when the smart cart drawer closes
let drawerOpen = document.body.classList.contains(SMART_CART_OPEN_CLASS);
new MutationObserver(() => {
  const open = document.body.classList.contains(SMART_CART_OPEN_CLASS);
  if (drawerOpen && !open) scheduleSync();
  drawerOpen = open;
}).observe(document.body, { attributes: true, attributeFilter: ['class'] });

// Back/forward cache restores an old page
window.addEventListener('pageshow', (event) => {
  if (event.persisted) scheduleSync();
});
